import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Plus,
  Edit,
  Trash2,
  Clock,
  Users,
  Calendar,
  MessageSquare,
  Download,
  Eye,
  CreditCard,
  FileText,
  Check,
  CheckCircle2,
  ArrowLeft,
  Wrench,
  Package,
  Briefcase,
  Archive,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import { EditLocationSheet } from "../EditLocationSheet";
import { AssignStaffSheet } from "./AssignStaffSheet";
import { AssignSubcontractorSheet } from "./AssignSubcontractorSheet";
import { ViewEmployeeAssignmentSheet } from "./ViewEmployeeAssignmentSheet";
import { EditEmployeeAssignmentSheet } from "./EditEmployeeAssignmentSheet";
import { ViewSubcontractorAssignmentSheet } from "./ViewSubcontractorAssignmentSheet";
import { EditSubcontractorAssignmentSheet } from "./EditSubcontractorAssignmentSheet";
import { AddProjectSheet } from "./AddProjectSheet";
import { EditProjectSheet } from "./EditProjectSheet";
import { ProjectDetailsSheet } from "./ProjectDetailsSheet";
import { AddSuppliesSheet } from "./AddSuppliesSheet";
import { EditSuppliesSheet } from "./EditSuppliesSheet";
import { LocationEquipmentTab } from "./LocationEquipmentTab";
import { ScheduleVisitSheet } from "./ScheduleVisitSheet";
import { LocationVisits } from "./LocationVisits";
import { EditVisitSheet } from "./EditVisitSheet";
import { AddLocationServiceSheet } from "./AddLocationServiceSheet";
import { EditLocationServiceSheet } from "./EditLocationServiceSheet";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { geocodeAddress } from "@/lib/map-utils";

interface ServiceLocation {
  _id: Id<"serviceLocations">;
  name: string;
  address: string;
  businessName: string;
  squareFootage: string;
  cleaningFrequency: string;
  serviceDays: string[];
  staffRequired?: number;
  coordinates?: { lat: number; lng: number };
  contactName?: string;
  contactTitle?: string;
  contactEmail?: string;
  contactPhone?: string;
}

interface Supply {
  _id: Id<"locationSupplies">;
  name: string;
  quantity: number;
  unit: string;
  lastRestocked: string;
  minimumRequired?: number;
  brand?: string;
  vendor?: string;
  costPerUnit?: number;
  storageLocation?: string;
  category?: string;
}

interface StaffMember {
  _id: Id<"locationEmployeeAssignments">;
  employeeName: string;
  position: string;
  shift: string;
  employeePhone: string;
}

interface SubcontractorAssignment {
  _id: Id<"locationSubcontractorAssignments">;
  subcontractorName: string;
  contactName: string;
  phone: string;
  email: string;
  services: string[];
  status: string;
}

interface Project {
  _id: Id<"locationProjects">;
  name: string;
  description?: string;
  status: string;
  startDate: string;
  dueDate: string;
  assignedToNames?: string[];
}

// Helper function to order service days from Sunday to Saturday
const orderServiceDays = (days: string[]) => {
  const dayOrder = {
    Sunday: 0,
    Sun: 0,
    Monday: 1,
    Mon: 1,
    Tuesday: 2,
    Tue: 2,
    Tues: 2,
    Wednesday: 3,
    Wed: 3,
    Thursday: 4,
    Thu: 4,
    Thur: 4,
    Thurs: 4,
    Friday: 5,
    Fri: 5,
    Saturday: 6,
    Sat: 6,
  };

  return days.sort((a, b) => {
    const orderA = dayOrder[a as keyof typeof dayOrder] ?? 999;
    const orderB = dayOrder[b as keyof typeof dayOrder] ?? 999;
    return orderA - orderB;
  });
};

// Helper function to format frequency strings
const formatFrequency = (frequency: string) => {
  if (!frequency) return "";

  return frequency
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/\b\w/g, (char: string) => char.toUpperCase()); // Capitalize first letter of each word
};

// Helper functions for staffing status
const getStaffingStatus = (currentStaff: number, requiredStaff: number) => {
  if (currentStaff >= requiredStaff) {
    return {
      status: "fully-staffed",
      label: "Fully Staffed",
      variant: "default" as const,
      className: "bg-green-100 text-green-800 border-green-200",
    };
  } else if (currentStaff === 0) {
    return {
      status: "no-staff",
      label: `${requiredStaff} Staff Needed`,
      variant: "destructive" as const,
      className: "bg-red-100 text-red-800 border-red-200",
    };
  } else {
    const needed = requiredStaff - currentStaff;
    return {
      status: "understaffed",
      label: `${needed} More Needed`,
      variant: "secondary" as const,
      className: "bg-yellow-100 text-yellow-800 border-yellow-200",
    };
  }
};

// Helper function for supply stock status
const getSupplyStockStatus = (quantity: number, minimumRequired?: number) => {
  if (quantity === 0) {
    return {
      status: "out-of-stock",
      label: "Out of Stock",
      className: "bg-red-100 text-red-800 border-red-200",
    };
  } else if (minimumRequired && quantity <= minimumRequired) {
    return {
      status: "low-stock",
      label: "Low Stock",
      className: "bg-yellow-100 text-yellow-800 border-yellow-200",
    };
  } else {
    return {
      status: "in-stock",
      label: "In Stock",
      className: "bg-green-100 text-green-800 border-green-200",
    };
  }
};

export default function ServiceLocationView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditLocationSheetOpen, setIsEditLocationSheetOpen] = useState(false);
  const [isAssignStaffSheetOpen, setIsAssignStaffSheetOpen] = useState(false);
  const [isAssignSubcontractorSheetOpen, setIsAssignSubcontractorSheetOpen] = useState(false);
  const [isViewEmployeeAssignmentSheetOpen, setIsViewEmployeeAssignmentSheetOpen] = useState(false);
  const [isEditEmployeeAssignmentSheetOpen, setIsEditEmployeeAssignmentSheetOpen] = useState(false);
  const [isViewSubcontractorAssignmentSheetOpen, setIsViewSubcontractorAssignmentSheetOpen] =
    useState(false);
  const [isEditSubcontractorAssignmentSheetOpen, setIsEditSubcontractorAssignmentSheetOpen] =
    useState(false);
  const [selectedEmployeeAssignment, setSelectedEmployeeAssignment] = useState<any>(null);
  const [selectedSubcontractorAssignment, setSelectedSubcontractorAssignment] = useState<any>(null);
  const [isAddProjectSheetOpen, setIsAddProjectSheetOpen] = useState(false);
  const [isEditProjectSheetOpen, setIsEditProjectSheetOpen] = useState(false);
  const [isProjectDetailsSheetOpen, setIsProjectDetailsSheetOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isAddSuppliesSheetOpen, setIsAddSuppliesSheetOpen] = useState(false);
  const [isEditSuppliesSheetOpen, setIsEditSuppliesSheetOpen] = useState(false);
  const [isScheduleVisitSheetOpen, setIsScheduleVisitSheetOpen] = useState(false);
  const [isEditVisitSheetOpen, setIsEditVisitSheetOpen] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState<Supply | null>(null);
  const [selectedVisit, setSelectedVisit] = useState<any>(null);
  const [isAddServiceSheetOpen, setIsAddServiceSheetOpen] = useState(false);
  const [isEditServiceSheetOpen, setIsEditServiceSheetOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<
    "overview" | "staff" | "subcontractors" | "projects" | "supplies" | "equipment" | "visits"
  >("overview");
  const [geocodedCoordinates, setGeocodedCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Check if user came from client view page
  const previousPage = sessionStorage.getItem("previousPage");
  const cameFromClient =
    previousPage && previousPage.startsWith("/clients/") && !previousPage.includes("/locations");

  // Fetch location data from Convex
  const location = useQuery(
    api.serviceLocations.getLocationById,
    id ? { id: id as Id<"serviceLocations"> } : "skip"
  );

  // Fetch related data
  const staff =
    useQuery(
      api.serviceLocations.getLocationStaff,
      id ? { locationId: id as Id<"serviceLocations"> } : "skip"
    ) ?? [];

  const subcontractors =
    useQuery(
      api.serviceLocations.getLocationSubcontractors,
      id ? { locationId: id as Id<"serviceLocations"> } : "skip"
    ) ?? [];

  // Mutations
  const removeStaffAssignment = useMutation(api.serviceLocations.removeStaffAssignment);
  const removeSubcontractorAssignment = useMutation(
    api.serviceLocations.removeSubcontractorAssignment
  );
  const deleteProject = useMutation(api.serviceLocations.deleteProject);
  const deleteSupplies = useMutation(api.serviceLocations.deleteLocationSupplies);

  // Handle remove staff assignment
  const handleRemoveStaff = async (
    assignmentId: Id<"locationEmployeeAssignments">,
    employeeName: string
  ) => {
    try {
      await removeStaffAssignment({ assignmentId });
      toast.success(`${employeeName} has been removed from this location`);
    } catch (error) {
      console.error("Error removing staff assignment:", error);
      toast.error("Failed to remove staff assignment");
    }
  };

  // Handle remove subcontractor assignment
  const handleRemoveSubcontractor = async (
    assignmentId: Id<"locationSubcontractorAssignments">,
    subcontractorName: string
  ) => {
    try {
      await removeSubcontractorAssignment({ assignmentId });
      toast.success(`${subcontractorName} has been removed from this location`);
    } catch (error) {
      console.error("Error removing subcontractor assignment:", error);
      toast.error("Failed to remove subcontractor assignment");
    }
  };

  // Handle remove project
  const handleRemoveProject = async (projectId: Id<"locationProjects">, projectName: string) => {
    try {
      await deleteProject({ projectId });
      toast.success(`Project "${projectName}" has been deleted`);
    } catch (error) {
      console.error("Error removing project:", error);
      toast.error("Failed to remove project");
    }
  };

  // Handle remove supplies
  const handleRemoveSupplies = async (supplyId: Id<"locationSupplies">, supplyName: string) => {
    try {
      await deleteSupplies({ supplyId });
      toast.success(`Supply "${supplyName}" has been deleted`);
    } catch (error) {
      console.error("Error removing supplies:", error);
      toast.error("Failed to remove supplies");
    }
  };

  // Handle view project details
  const handleViewProject = (project: any) => {
    setSelectedProject(project);
    setIsProjectDetailsSheetOpen(true);
  };

  // Handle edit project
  const handleEditProject = (project: any) => {
    setSelectedProject(project);
    setIsEditProjectSheetOpen(true);
  };

  // Handle edit supplies
  const handleEditSupplies = (supply: Supply) => {
    setSelectedSupply(supply);
    setIsEditSuppliesSheetOpen(true);
  };

  // Handle edit visit
  const handleEditVisit = (visit: any) => {
    setSelectedVisit(visit);
    setIsEditVisitSheetOpen(true);
  };

  // Handle view employee assignment
  const handleViewEmployeeAssignment = (assignment: any) => {
    setSelectedEmployeeAssignment(assignment);
    setIsViewEmployeeAssignmentSheetOpen(true);
  };

  // Handle edit employee assignment
  const handleEditEmployeeAssignment = (assignment: any) => {
    setSelectedEmployeeAssignment(assignment);
    setIsEditEmployeeAssignmentSheetOpen(true);
  };

  // Handle view subcontractor assignment
  const handleViewSubcontractorAssignment = (assignment: any) => {
    setSelectedSubcontractorAssignment(assignment);
    setIsViewSubcontractorAssignmentSheetOpen(true);
  };

  // Handle edit subcontractor assignment
  const handleEditSubcontractorAssignment = (assignment: any) => {
    setSelectedSubcontractorAssignment(assignment);
    setIsEditSubcontractorAssignmentSheetOpen(true);
  };

  // Handle assignment updated callback
  const handleAssignmentUpdated = () => {
    // This will trigger a re-fetch of the data
    // The useQuery hooks will automatically update
  };

  const archiveLocation = useMutation(api.serviceLocations.archive);

  const handleArchiveLocation = async () => {
    if (!id) return;
    try {
      await archiveLocation({ id: id as Id<"serviceLocations"> });
      toast.success(`${location?.name || "Location"} archived successfully`);
      if (cameFromClient) {
        navigate(previousPage);
      } else {
        navigate("/clients/locations");
      }
    } catch (error) {
      console.error("Failed to archive location:", error);
      toast.error("Failed to archive location");
    }
  };

  // Fetch bid services for this location
  const bidServices =
    useQuery(
      api.serviceLocations.getLocationBidServices,
      id ? { locationId: id as Id<"serviceLocations"> } : "skip"
    ) ?? [];

  // Fetch manual services for this location
  const manualServices =
    useQuery(
      api.serviceLocations.getLocationServices,
      id ? { locationId: id as Id<"serviceLocations"> } : "skip"
    ) ?? [];

  const projects =
    useQuery(
      api.serviceLocations.getLocationProjects,
      id ? { locationId: id as Id<"serviceLocations"> } : "skip"
    ) ?? [];

  const supplies =
    useQuery(
      api.serviceLocations.getLocationSupplies,
      id ? { locationId: id as Id<"serviceLocations"> } : "skip"
    ) ?? [];

  // Mutation for updating coordinates
  const updateLocation = useMutation(api.serviceLocations.update);
  const deleteService = useMutation(api.serviceLocations.deleteLocationService);

  // Fallback geocoding: if location doesn't have coordinates, geocode the address
  useEffect(() => {
    if (location && !location.coordinates && !isGeocoding && !geocodedCoordinates) {
      setIsGeocoding(true);
      geocodeAddress(location.address)
        .then(async (coords) => {
          if (coords) {
            setGeocodedCoordinates(coords);
            // Update the location in the database with the new coordinates
            try {
              await updateLocation({
                id: location._id,
                coordinates: coords,
              });
            } catch (error) {
              console.error("Failed to update location coordinates:", error);
            }
          }
        })
        .catch((error) => {
          console.error("Geocoding failed:", error);
        })
        .finally(() => {
          setIsGeocoding(false);
        });
    }
  }, [location, isGeocoding, geocodedCoordinates, updateLocation]);

  if (!location) {
    return (
      <DashboardLayout activeTab="clients">
        <div className="p-6">
          {cameFromClient ? (
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => {
                sessionStorage.removeItem("previousPage");
                navigate(previousPage);
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Client
            </Button>
          ) : (
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => navigate("/clients/locations")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Locations
            </Button>
          )}
          <Card className="p-6">
            <div className="py-8 text-center">
              <h2 className="mb-2 text-xl font-semibold">Location Not Found</h2>
              <p className="text-gray-500">
                The location you're looking for doesn't exist or has been removed.
              </p>
            </div>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeTab="clients">
      <div className="space-y-6">
        {/* Back Button */}
        <div className="mb-2 flex items-center gap-3">
          {cameFromClient ? (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1"
              onClick={() => {
                sessionStorage.removeItem("previousPage");
                navigate(previousPage);
              }}
            >
              <ArrowLeft className="h-4 w-4" /> Back to Client
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1"
              onClick={() => navigate("/clients/locations")}
            >
              <ArrowLeft className="h-4 w-4" /> Back to Locations
            </Button>
          )}
        </div>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-gray-900">{location.name}</h1>
              {location.status && (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    {
                      active: "bg-green-100 text-green-700",
                      inactive: "bg-gray-100 text-gray-700",
                      pending: "bg-yellow-100 text-yellow-700",
                    }[location.status as "active" | "inactive" | "pending"] ||
                    "bg-gray-100 text-gray-700"
                  }`}
                >
                  {location.status.charAt(0).toUpperCase() + location.status.slice(1)}
                </span>
              )}
            </div>
            <div className="mt-2 flex items-center gap-2 text-gray-500">
              <Building2 className="h-4 w-4" />
              <span>{location.businessName}</span>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="gap-2" onClick={handleArchiveLocation}>
              <Archive className="h-4 w-4" /> Archive Location
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setIsScheduleVisitSheetOpen(true)}
            >
              <Calendar className="h-4 w-4" /> Schedule Visit
            </Button>

            <Button className="gap-2" onClick={() => setIsEditLocationSheetOpen(true)}>
              <Edit className="h-4 w-4" /> Edit Location
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              className={`border-b-2 px-1 py-4 text-sm font-medium ${
                activeTab === "overview"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </button>
            <button
              className={`border-b-2 px-1 py-4 text-sm font-medium ${
                activeTab === "staff"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("staff")}
            >
              Assigned Staff
            </button>
            <button
              className={`border-b-2 px-1 py-4 text-sm font-medium ${
                activeTab === "subcontractors"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("subcontractors")}
            >
              Assigned Subcontractors
            </button>
            <button
              className={`border-b-2 px-1 py-4 text-sm font-medium ${
                activeTab === "projects"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("projects")}
            >
              Projects
            </button>
            <button
              className={`border-b-2 px-1 py-4 text-sm font-medium ${
                activeTab === "supplies"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("supplies")}
            >
              Supplies
            </button>
            <button
              className={`border-b-2 px-1 py-4 text-sm font-medium ${
                activeTab === "equipment"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("equipment")}
            >
              Equipment
            </button>
            <button
              className={`border-b-2 px-1 py-4 text-sm font-medium ${
                activeTab === "visits"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("visits")}
            >
              Visits
            </button>
          </div>
        </div>

        {activeTab === "overview" && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {/* Left Column - Details */}
            <div className="space-y-6">
              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Location Information</h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                      <Building2 className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">{location.name}</h3>
                      <p className="text-sm text-gray-500">{location.address}</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h3 className="text-sm font-medium">Contact Information</h3>
                    {location.contactName || location.contactEmail || location.contactPhone ? (
                      <div className="space-y-2">
                        {location.contactName && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Users className="h-4 w-4 text-gray-400" />
                            <span>
                              {location.contactTitle
                                ? `${location.contactTitle}: ${location.contactName}`
                                : location.contactName}
                            </span>
                          </div>
                        )}
                        {location.contactEmail && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <a
                              href={`mailto:${location.contactEmail}`}
                              className="text-blue-600 hover:underline"
                            >
                              {location.contactEmail}
                            </a>
                          </div>
                        )}
                        {location.contactPhone && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Phone className="h-4 w-4 text-gray-400" />
                            <span>{location.contactPhone}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No contact information available</p>
                    )}
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Column - Service Schedule & Map */}
            <div className="md:col-span-2">
              <Card className="mb-6 p-6">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Services</h2>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setIsAddServiceSheetOpen(true)}
                    >
                      <Plus className="h-4 w-4" /> Add Service
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="mb-4">
                    <h3 className="mb-2 text-sm font-medium text-gray-500">Service Days</h3>
                    <div className="flex flex-wrap gap-2">
                      {location.serviceDays && location.serviceDays.length > 0
                        ? orderServiceDays(location.serviceDays).map((day) => (
                            <span
                              key={day}
                              className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
                            >
                              {day}
                            </span>
                          ))
                        : "No days specified"}
                    </div>
                  </div>

                  {/* Check if there are any services to display */}
                  {bidServices.length > 0 || manualServices.length > 0 ? (
                    <>
                      {/* Cleaning Services */}
                      {(bidServices.some((s: { type: string }) => s.type === "cleaning") ||
                        manualServices.some((s: { type: string }) => s.type === "cleaning")) && (
                        <>
                          <h3 className="mb-2 text-sm font-medium text-gray-500">
                            Cleaning Services
                          </h3>
                          <div className="space-y-3">
                            {/* Bid-based cleaning services */}
                            {bidServices
                              .filter((service: { type: string }) => service.type === "cleaning")
                              .map(
                                (service: {
                                  _id: string;
                                  type: string;
                                  name: string;
                                  frequency: string;
                                  description?: string;
                                }) => (
                                  <div
                                    key={service._id}
                                    className="rounded-lg border border-gray-100 bg-gray-50 p-3"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <p className="font-medium">{service.name}</p>
                                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                                            From Bid
                                          </span>
                                        </div>
                                        <p className="text-sm text-gray-500">
                                          {formatFrequency(service.frequency)}
                                        </p>
                                        {service.description && (
                                          <p className="mt-1 text-sm text-gray-600">
                                            {service.description}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                            {/* Manual cleaning services */}
                            {manualServices
                              .filter((service: { type: string }) => service.type === "cleaning")
                              .map(
                                (service: {
                                  _id: string;
                                  type: string;
                                  name: string;
                                  frequency: string;
                                  description?: string;
                                }) => (
                                  <div
                                    key={service._id}
                                    className="rounded-lg border border-gray-100 bg-gray-50 p-3"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <p className="font-medium">{service.name}</p>
                                        <p className="text-sm text-gray-500">
                                          {formatFrequency(service.frequency)}
                                        </p>
                                        {service.description && (
                                          <p className="mt-1 text-sm text-gray-600">
                                            {service.description}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setSelectedService(service);
                                            setIsEditServiceSheetOpen(true);
                                          }}
                                        >
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={async () => {
                                            try {
                                              await deleteService({
                                                serviceId: service._id as Id<"locationServices">,
                                              });
                                              toast.success("Service deleted");
                                            } catch (error) {
                                              toast.error("Failed to delete service");
                                            }
                                          }}
                                        >
                                          <Trash2 className="h-4 w-4 text-red-500" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                          </div>
                        </>
                      )}

                      {/* Floorcare Services */}
                      {(bidServices.some((s: { type: string }) => s.type === "floorcare") ||
                        manualServices.some((s: { type: string }) => s.type === "floorcare")) && (
                        <>
                          <h3 className="mb-2 mt-4 text-sm font-medium text-gray-500">
                            Floorcare Services
                          </h3>
                          <div className="space-y-3">
                            {/* Bid-based floorcare services */}
                            {bidServices
                              .filter((service: { type: string }) => service.type === "floorcare")
                              .map(
                                (service: {
                                  _id: string;
                                  type: string;
                                  name: string;
                                  frequency: string;
                                  description?: string;
                                }) => (
                                  <div
                                    key={service._id}
                                    className="rounded-lg border border-gray-100 bg-gray-50 p-3"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <p className="font-medium">{service.name}</p>
                                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                                            From Bid
                                          </span>
                                        </div>
                                        <p className="text-sm text-gray-500">
                                          {formatFrequency(service.frequency)}
                                        </p>
                                        {service.description && (
                                          <p className="mt-1 text-sm text-gray-600">
                                            {service.description}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                            {/* Manual floorcare services */}
                            {manualServices
                              .filter((service: { type: string }) => service.type === "floorcare")
                              .map(
                                (service: {
                                  _id: string;
                                  type: string;
                                  name: string;
                                  frequency: string;
                                  description?: string;
                                }) => (
                                  <div
                                    key={service._id}
                                    className="rounded-lg border border-gray-100 bg-gray-50 p-3"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <p className="font-medium">{service.name}</p>
                                        <p className="text-sm text-gray-500">
                                          {formatFrequency(service.frequency)}
                                        </p>
                                        {service.description && (
                                          <p className="mt-1 text-sm text-gray-600">
                                            {service.description}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setSelectedService(service);
                                            setIsEditServiceSheetOpen(true);
                                          }}
                                        >
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={async () => {
                                            try {
                                              await deleteService({
                                                serviceId: service._id as Id<"locationServices">,
                                              });
                                              toast.success("Service deleted");
                                            } catch (error) {
                                              toast.error("Failed to delete service");
                                            }
                                          }}
                                        >
                                          <Trash2 className="h-4 w-4 text-red-500" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <EmptyState
                      icon={Briefcase}
                      title="No services configured"
                      description="Add a service manually or attach a bid to this location."
                    >
                      <Button
                        variant="outline"
                        className="mt-4 gap-2"
                        onClick={() => setIsAddServiceSheetOpen(true)}
                      >
                        <Plus className="h-4 w-4" /> Add Your First Service
                      </Button>
                    </EmptyState>
                  )}
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Location Map</h2>
                {(() => {
                  const coords = location.coordinates || geocodedCoordinates;
                  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

                  if (isGeocoding) {
                    return (
                      <div className="relative h-[300px] w-full overflow-hidden rounded-md bg-gray-100">
                        <div className="flex h-full items-center justify-center text-gray-500">
                          <div className="text-center">
                            <MapPin className="mx-auto mb-2 h-6 w-6 animate-pulse" />
                            <span>Loading map...</span>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (coords) {
                    if (!apiKey) {
                      return (
                        <div className="flex h-[300px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-4 text-center">
                          <MapPin className="mb-2 h-6 w-6 text-amber-600" />
                          <h3 className="text-sm font-medium text-gray-900">API Key Missing</h3>
                          <p className="mt-1 text-xs text-gray-500">
                            Google Maps API key is not configured.
                          </p>
                        </div>
                      );
                    }
                    return (
                      <APIProvider apiKey={apiKey}>
                        <div className="h-[300px] w-full overflow-hidden rounded-md">
                          <Map
                            defaultCenter={{ lat: coords.lat, lng: coords.lng }}
                            defaultZoom={15}
                            gestureHandling="greedy"
                            disableDefaultUI={false}
                          >
                            <Marker position={{ lat: coords.lat, lng: coords.lng }} />
                          </Map>
                        </div>
                      </APIProvider>
                    );
                  }

                  return (
                    <div className="relative h-[300px] w-full overflow-hidden rounded-md bg-gray-100">
                      <div className="flex h-full items-center justify-center text-gray-500">
                        <MapPin className="mr-2 h-6 w-6" />
                        <span>{location.address}</span>
                      </div>
                    </div>
                  );
                })()}
              </Card>
            </div>
          </div>
        )}

        {activeTab === "staff" && (
          <div className="space-y-6">
            <Card className="p-6">
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h2 className="text-lg font-semibold">Assigned Staff</h2>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-500" />
                    {location.staffRequired && location.staffRequired > 0 ? (
                      <>
                        <span className="text-sm text-gray-600">
                          {staff.length} of {location.staffRequired} assigned
                        </span>
                        <Badge
                          className={
                            getStaffingStatus(staff.length, location.staffRequired).className
                          }
                        >
                          {getStaffingStatus(staff.length, location.staffRequired).label}
                        </Badge>
                      </>
                    ) : (
                      <span className="text-sm text-gray-500">
                        {staff.length} staff assigned • No requirement set
                      </span>
                    )}
                  </div>
                </div>
                <Button className="gap-2" onClick={() => setIsAssignStaffSheetOpen(true)}>
                  <Plus className="h-4 w-4" /> Assign Staff
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead>Shift</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staff.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <EmptyState
                          icon={Users}
                          title="No staff assigned"
                          description="No staff assigned to this location yet."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    staff.map((staffMember: StaffMember) => (
                      <TableRow key={staffMember._id}>
                        <TableCell>
                          <div className="font-medium">{staffMember.employeeName}</div>
                        </TableCell>
                        <TableCell>{staffMember.position}</TableCell>
                        <TableCell>{staffMember.shift}</TableCell>
                        <TableCell>{staffMember.employeePhone}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewEmployeeAssignment(staffMember)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditEmployeeAssignment(staffMember)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleRemoveStaff(staffMember._id, staffMember.employeeName)
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}

        {activeTab === "subcontractors" && (
          <div className="space-y-6">
            <Card className="p-6">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Assigned Subcontractors</h2>
                <Button className="gap-2" onClick={() => setIsAssignSubcontractorSheetOpen(true)}>
                  <Plus className="h-4 w-4" /> Assign Subcontractor
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company Name</TableHead>
                    <TableHead>Contact Person</TableHead>
                    <TableHead>Phone/Email</TableHead>
                    <TableHead>Services</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subcontractors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <EmptyState
                          icon={Building2}
                          title="No subcontractors assigned"
                          description="No subcontractors assigned to this location yet."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    subcontractors.map((subcontractor: SubcontractorAssignment) => (
                      <TableRow key={subcontractor._id}>
                        <TableCell>
                          <div className="font-medium">{subcontractor.subcontractorName}</div>
                        </TableCell>
                        <TableCell>{subcontractor.contactName}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{subcontractor.phone}</div>
                            <div className="text-gray-500">{subcontractor.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {subcontractor.services.map((service: string, index: number) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {service}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={`${
                              subcontractor.status === "active"
                                ? "bg-green-100 text-green-800"
                                : subcontractor.status === "scheduled"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {subcontractor.status.charAt(0).toUpperCase() +
                              subcontractor.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewSubcontractorAssignment(subcontractor)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditSubcontractorAssignment(subcontractor)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleRemoveSubcontractor(
                                  subcontractor._id,
                                  subcontractor.subcontractorName
                                )
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}

        {activeTab === "projects" && (
          <div className="space-y-6">
            <Card className="p-6">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Projects</h2>
                <Button className="gap-2" onClick={() => setIsAddProjectSheetOpen(true)}>
                  <Plus className="h-4 w-4" /> Add Project
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Project Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Assigned Team</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <EmptyState
                          icon={Briefcase}
                          title="No projects"
                          description="No projects for this location yet."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    projects.map((project: Project) => (
                      <TableRow key={project._id}>
                        <TableCell>
                          <div className="font-medium">{project.name}</div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs truncate text-sm text-gray-600">
                            {project.description || "No description"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                              project.status === "Completed"
                                ? "bg-green-100 text-green-700"
                                : project.status === "In Progress"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {project.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{project.startDate}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{project.dueDate}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {project.assignedToNames?.length ||
                            (project as any).assignedSubcontractorNames?.length ? (
                              <div className="space-y-1">
                                {(project.assignedToNames?.length ?? 0) > 0 && (
                                  <div className="text-blue-600">
                                    Employees: {project.assignedToNames?.join(", ")}
                                  </div>
                                )}
                                {(project as any).assignedSubcontractorNames?.length > 0 && (
                                  <div className="text-green-600">
                                    Subcontractors:{" "}
                                    {(project as any).assignedSubcontractorNames.join(", ")}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-500">No team assigned</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewProject(project)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditProject(project)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveProject(project._id, project.name)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}

        {activeTab === "supplies" && (
          <div className="space-y-6">
            <Card className="p-6">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Supplies Inventory</h2>
                <Button className="gap-2" onClick={() => setIsAddSuppliesSheetOpen(true)}>
                  <Plus className="h-4 w-4" /> Add Supplies
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Cost Per Unit</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Storage Location</TableHead>
                    <TableHead>Last Restocked</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {supplies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11}>
                        <EmptyState
                          icon={Package}
                          title="No supplies tracked"
                          description="No supplies tracked for this location yet."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    supplies.map((supply: Supply) => (
                      <TableRow key={supply._id}>
                        <TableCell>
                          <div className="font-medium">{supply.name}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {supply.category
                              ? supply.category
                                  .replace(/-/g, " ")
                                  .replace(/\b\w/g, (l: string) => l.toUpperCase())
                              : "-"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{supply.brand || "-"}</div>
                        </TableCell>
                        <TableCell>{supply.quantity}</TableCell>
                        <TableCell>{supply.unit}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {supply.costPerUnit ? `$${supply.costPerUnit.toFixed(2)}` : "-"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{supply.vendor || "-"}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{supply.storageLocation || "-"}</div>
                        </TableCell>
                        <TableCell>{supply.lastRestocked}</TableCell>
                        <TableCell>
                          <Badge
                            className={
                              getSupplyStockStatus(supply.quantity, supply.minimumRequired)
                                .className
                            }
                          >
                            {getSupplyStockStatus(supply.quantity, supply.minimumRequired).label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditSupplies(supply)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveSupplies(supply._id, supply.name)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}

        {activeTab === "equipment" && location && (
          <LocationEquipmentTab locationId={location._id} />
        )}

        {activeTab === "visits" && location && (
          <div className="space-y-6">
            <Card className="p-6">
              <LocationVisits
                locationId={location._id}
                onScheduleVisit={() => setIsScheduleVisitSheetOpen(true)}
                onEditVisit={handleEditVisit}
              />
            </Card>
          </div>
        )}
      </div>

      {location && (
        <EditLocationSheet
          isOpen={isEditLocationSheetOpen}
          onClose={() => setIsEditLocationSheetOpen(false)}
          location={location}
        />
      )}

      {location && (
        <AssignStaffSheet
          isOpen={isAssignStaffSheetOpen}
          onClose={() => setIsAssignStaffSheetOpen(false)}
          locationId={location._id}
          onStaffAssigned={() => {
            // The staff list will automatically refresh due to the useQuery
            // No additional action needed since Convex handles reactivity
          }}
        />
      )}

      {location && (
        <AssignSubcontractorSheet
          isOpen={isAssignSubcontractorSheetOpen}
          onClose={() => setIsAssignSubcontractorSheetOpen(false)}
          locationId={location._id}
          onSubcontractorAssigned={() => {
            // The subcontractor list will automatically refresh due to the useQuery
            // No additional action needed since Convex handles reactivity
          }}
        />
      )}

      {location && (
        <AddProjectSheet
          isOpen={isAddProjectSheetOpen}
          onClose={() => setIsAddProjectSheetOpen(false)}
          locationId={location._id}
          onProjectAdded={() => {
            // The projects list will automatically refresh due to the useQuery
            // No additional action needed since Convex handles reactivity
          }}
        />
      )}

      <EditProjectSheet
        isOpen={isEditProjectSheetOpen}
        onClose={() => {
          setIsEditProjectSheetOpen(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        onProjectUpdated={() => {
          // The projects list will automatically refresh due to the useQuery
          // No additional action needed since Convex handles reactivity
        }}
      />

      <ProjectDetailsSheet
        isOpen={isProjectDetailsSheetOpen}
        onClose={() => {
          setIsProjectDetailsSheetOpen(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        onEdit={() => {
          // Close details sheet and open edit sheet
          setIsProjectDetailsSheetOpen(false);
          setIsEditProjectSheetOpen(true);
        }}
      />

      {location && (
        <AddSuppliesSheet
          isOpen={isAddSuppliesSheetOpen}
          onClose={() => setIsAddSuppliesSheetOpen(false)}
          locationId={location._id}
          onSuppliesAdded={() => {
            // The supplies list will automatically refresh due to the useQuery
            // No additional action needed since Convex handles reactivity
          }}
        />
      )}

      <EditSuppliesSheet
        isOpen={isEditSuppliesSheetOpen}
        onClose={() => {
          setIsEditSuppliesSheetOpen(false);
          setSelectedSupply(null);
        }}
        supply={selectedSupply}
        onSuppliesUpdated={() => {
          // The supplies list will automatically refresh due to the useQuery
          // No additional action needed since Convex handles reactivity
        }}
      />

      {location && (
        <ScheduleVisitSheet
          isOpen={isScheduleVisitSheetOpen}
          onClose={() => setIsScheduleVisitSheetOpen(false)}
          locationId={location._id}
          businessId={location.businessId}
          locationName={location.name}
          defaultAddress={location.address}
          onVisitScheduled={() => {
            // Visit scheduled successfully, sheet will close automatically
            // No additional action needed since Convex handles reactivity
          }}
        />
      )}

      <EditVisitSheet
        isOpen={isEditVisitSheetOpen}
        onClose={() => {
          setIsEditVisitSheetOpen(false);
          setSelectedVisit(null);
        }}
        visit={selectedVisit}
        onVisitUpdated={() => {
          setIsEditVisitSheetOpen(false);
          setSelectedVisit(null);
          // Visit list will automatically refresh due to Convex reactivity
        }}
      />

      {/* Employee Assignment Sheets */}
      <ViewEmployeeAssignmentSheet
        isOpen={isViewEmployeeAssignmentSheetOpen}
        onClose={() => {
          setIsViewEmployeeAssignmentSheetOpen(false);
          setSelectedEmployeeAssignment(null);
        }}
        assignment={selectedEmployeeAssignment}
        onEdit={() => {
          setIsViewEmployeeAssignmentSheetOpen(false);
          setIsEditEmployeeAssignmentSheetOpen(true);
        }}
      />

      <EditEmployeeAssignmentSheet
        isOpen={isEditEmployeeAssignmentSheetOpen}
        onClose={() => {
          setIsEditEmployeeAssignmentSheetOpen(false);
          setSelectedEmployeeAssignment(null);
        }}
        assignment={selectedEmployeeAssignment}
        onAssignmentUpdated={handleAssignmentUpdated}
      />

      {/* Subcontractor Assignment Sheets */}
      <ViewSubcontractorAssignmentSheet
        isOpen={isViewSubcontractorAssignmentSheetOpen}
        onClose={() => {
          setIsViewSubcontractorAssignmentSheetOpen(false);
          setSelectedSubcontractorAssignment(null);
        }}
        assignment={selectedSubcontractorAssignment}
        onEdit={() => {
          setIsViewSubcontractorAssignmentSheetOpen(false);
          setIsEditSubcontractorAssignmentSheetOpen(true);
        }}
      />

      <EditSubcontractorAssignmentSheet
        isOpen={isEditSubcontractorAssignmentSheetOpen}
        onClose={() => {
          setIsEditSubcontractorAssignmentSheetOpen(false);
          setSelectedSubcontractorAssignment(null);
        }}
        assignment={selectedSubcontractorAssignment}
        onAssignmentUpdated={handleAssignmentUpdated}
      />

      {/* Location Service Sheets */}
      {location && (
        <AddLocationServiceSheet
          isOpen={isAddServiceSheetOpen}
          onClose={() => setIsAddServiceSheetOpen(false)}
          locationId={location._id}
          onSuccess={() => {
            // Services will automatically refresh due to Convex reactivity
          }}
        />
      )}

      <EditLocationServiceSheet
        isOpen={isEditServiceSheetOpen}
        onClose={() => {
          setIsEditServiceSheetOpen(false);
          setSelectedService(null);
        }}
        service={selectedService}
        onSuccess={() => {
          // Services will automatically refresh due to Convex reactivity
        }}
      />
    </DashboardLayout>
  );
}
